// +route.ts
import type { PageContext } from 'vike/types';
import { parseJobDetailRoutePath } from '../../../../utils/routing/parse-job-detail-route-path';
import { isLegacyBase64JobId } from './utils/legacyJobUrl';

/**
 * Route guard for job detail pages.
 * Validates that the route matches the expected pattern and that @jobPath is a valid job path.
 *
 * This prevents WordPress paths and other invalid paths from being matched as job routes.
 */
export function route(pageContext: PageContext) {
  const pathToCheck = (pageContext as { urlLogical?: string; urlPathname?: string }).urlLogical
    ?? pageContext.urlPathname;

  if (!pathToCheck) {
    return false;
  }

  const parsed = parseJobDetailRoutePath(pathToCheck);
  if (!parsed) {
    return false;
  }

  const { jobPath, jobId, companyId, companyIdRaw, seoSlug } = parsed;

  if (!jobId) {
    return false;
  }

  const suspiciousPatterns = [
    /\.(php|html|htm|js|css|xml|json|png|jpg|gif|svg|ico|woff|woff2|ttf|eot|zip|tar|gz)$/i,
    /^(wp-|vendor|modules|css|js|images|uploads|cgi-bin|\.well-known|\.cache|\.tmb|\.trash|queue|admin|includes|themes|plugins|content)/i,
    /^(js|css|php|html|xml|json|png|jpg|gif|svg|ico|woff|ttf|zip|tar|gz|index|admin|config|security|bypass|byp|utils|smoothness|nette|elements)$/i,
    /[\/\\]/,
    /\.\./,
  ];

  for (const pattern of suspiciousPatterns) {
    if (pattern.test(jobId) || (companyId && pattern.test(companyId))) {
      return false;
    }
  }

  if (companyId && companyId.length < 2) {
    return false;
  }

  if (jobId.length < 3) {
    return false;
  }

  if (isLegacyBase64JobId(jobId)) {
    return false;
  }

  if (seoSlug && /\.(php|html|htm|js|css|xml|json|png|jpg|gif|svg|ico)$/i.test(seoSlug)) {
    return false;
  }

  return {
    routeParams: {
      jobPath,
      jobId,
      companyId,
      companyIdRaw: companyIdRaw || '',
      seoSlug: seoSlug || '',
    },
  };
}
